import { Observable, forkJoin, from, map } from "rxjs";

import { services } from "@progressive-development/pd-spa-helper";
import type { IDatabaseProvider } from "@progressive-development/pd-provider-interfaces";
import type { ris } from "@progressive-development/politik-model";

import {
  COL_MEETINGS,
  COL_MITGLIEDSCHAFTEN,
  COL_PAPERS,
  COL_VERZEICHNISSE,
  DOK_GREMIEN_KENNZAHLEN,
} from "../../../config/collections.js";

/** Wie viele vergangene Sitzungen eine Gremiumsseite zeigt. */
export const ANZAHL_SITZUNGEN = 12;
/** Wie viele zuletzt beratene Drucksachen eine Gremiumsseite zeigt. */
export const ANZAHL_DRUCKSACHEN = 10;
/** Obergrenze der Mitgliedschafts-Zeilen je Gremium — der Rat hat seit 2004 etwa 250. */
const ANZAHL_MITGLIEDSCHAFTEN = 600;

/**
 * Was JEDES Gremium liest — Bezirksvertretung, Ausschuss, Rat: seine
 * Sitzungen, die zuletzt hier beratenen Drucksachen und die Kennzahlen der
 * Wahlperiode. Der Unterschied zwischen den Gremien ist einer der Daten, nicht
 * des Verhaltens; was nur eine Art Gremium hat (die BV-Sitzverteilung), bleibt
 * in deren Modul.
 *
 * Sitzungen: ZWEI ABFRAGEN, GETRENNT NACH RICHTUNG — kommende aufsteigend,
 * damit die nächste oben steht; vergangene absteigend, damit die letzte oben
 * steht. In beiden Fällen ist die Sitzung, die dem Heute am nächsten liegt,
 * die gesuchte. Die Grenze ist der TAG, nicht der Zeitpunkt: Eine Sitzung, die
 * heute um 17 Uhr beginnt, gehört auch um 18 Uhr noch zu den kommenden.
 *
 * ZWEI INDIZES, nicht einer: Ein zusammengesetzter Firestore-Index bedient nur
 * die VOLLSTÄNDIG umgekehrte Richtung — der Spiegel von (organizationId ASC,
 * start ASC) ist (organizationId DESC, start DESC), gebraucht wird (ASC, DESC).
 */
export class GremienService {
  private get _db(): IDatabaseProvider {
    const db = services.database;
    if (!db) throw new Error("Kein Datenbank-Provider konfiguriert (provider-setup.ts).");
    return db;
  }

  /** Die Kennzahlen ALLER Gremien — ein Dokument, ein Lesevorgang. */
  public ladeKennzahlen(): Observable<ris.GremienKennzahlenDokument | undefined> {
    return from(
      this._db.getDocument<ris.GremienKennzahlenDokument>(
        COL_VERZEICHNISSE,
        DOK_GREMIEN_KENNZAHLEN,
      ),
    ).pipe(map((snap) => (snap.exists ? snap.data : undefined)));
  }

  /**
   * Alle Mitgliedschaften eines Gremiums — auch beendete.
   *
   * OHNE Sortierfeld und ohne Zeitfilter: Laufende Zeilen haben kein Enddatum,
   * und ein Filter darauf bräuchte einen Index für etwas, das ein Filter im
   * Browser über wenige hundert Zeilen genauso kann. Was „laufend" heißt,
   * entscheidet `gremium-besetzung.ts` — prüfbar.
   */
  public ladeBesetzung(gremiumId: string): Observable<Besetzung> {
    return from(
      this._db.getCollection<ris.Membership>(COL_MITGLIEDSCHAFTEN, {
        constraints: [{ type: "where", field: "organizationId", operator: "==", value: gremiumId }],
        limit: ANZAHL_MITGLIEDSCHAFTEN,
      }),
    ).pipe(map((seite) => ({ fuer: gremiumId, items: seite.documents.map((d) => d.data) })));
  }

  public ladeSitzungen(gremiumId: string, abTag: string): Observable<Sitzungen> {
    const fuerRichtung = (operator: ">=" | "<", richtung: "asc" | "desc", anzahl: number) =>
      from(
        this._db.getCollection<ris.Meeting>(COL_MEETINGS, {
          constraints: [
            { type: "where", field: "organizationId", operator: "==", value: gremiumId },
            { type: "where", field: "start", operator, value: abTag },
          ],
          orderBy: [{ field: "start", direction: richtung }],
          limit: anzahl,
        }),
      ).pipe(map((seite) => seite.documents.map((d) => d.data)));

    return forkJoin({
      kommend: fuerRichtung(">=", "asc", ANZAHL_SITZUNGEN),
      vergangen: fuerRichtung("<", "desc", ANZAHL_SITZUNGEN),
    }).pipe(map((s) => ({ fuer: gremiumId, ...s })));
  }

  /**
   * Die Drucksachen, die dieses Gremium ZULETZT beraten hat.
   *
   * Über `letzteStation.gremiumId` — die Station, an der der Vorgang derzeit
   * steht. Nicht über die Vereinigung der Bezirks-Anker: Die enthielte auch,
   * was der Rat für einen Bezirk entschieden hat, und die Frage hier lautet
   * „was macht dieses Gremium", nicht „was betrifft diesen Ort".
   */
  public ladeDrucksachen(gremiumId: string): Observable<GremiumDrucksachen> {
    return from(
      this._db.getCollection<ris.Paper>(COL_PAPERS, {
        constraints: [
          { type: "where", field: "letzteStation.gremiumId", operator: "==", value: gremiumId },
        ],
        orderBy: [{ field: "date", direction: "desc" }],
        limit: ANZAHL_DRUCKSACHEN,
      }),
    ).pipe(map((seite) => ({ fuer: gremiumId, items: seite.documents.map((d) => d.data) })));
  }
}

/** Sitzungen eines Gremiums, nach Richtung getrennt. */
export interface Sitzungen {
  /** Gremium-Kennung, für die geladen wurde. */
  fuer: string;
  kommend: ris.Meeting[];
  vergangen: ris.Meeting[];
}

/** Alle Mitgliedschaften eines Gremiums, laufende und beendete. */
export interface Besetzung {
  fuer: string;
  items: ris.Membership[];
}

/** Die zuletzt hier beratenen Drucksachen. */
export interface GremiumDrucksachen {
  fuer: string;
  items: ris.Paper[];
}

/**
 * Der Hinweis der Quelle zur Tagesordnung (etwa: Nicht-Öffentliches fehlt).
 * Er ist auf allen Sitzungen derselbe; genommen wird der erste, der da ist.
 */
export function sitzungHinweis(sitzungen: Sitzungen | undefined): string {
  const alle = [...(sitzungen?.kommend ?? []), ...(sitzungen?.vergangen ?? [])];
  return alle.find((s) => s.hinweisTagesordnung)?.hinweisTagesordnung ?? "";
}

export const gremienService = new GremienService();
